<template>
  <component
    :is="exercise_id"
    ref="inner"
    v-model="localValue"
    :context="context"
    :show_advanced="show_advanced"
    :customization="customization"
    v-bind="$attrs"
    v-on="$listeners"
  />
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import { getExerciseDefaultConfig } from "../classes/exercise";
import { DEFAULT_EXERCISE_CONFIG_CONTEXT, isExerciseConfigContext } from "../lib/exercise_config_context";
import OpticalGymConfigForms from "../../../exercises/OpticalGymConfigForms.js";

export default {
  name: "ExerciseConfigForm",
  inheritAttrs: false,
  components: Object.assign({}, OpticalGymConfigForms),
  props: {
    value: {
      type: Object,
      required: true,
    },
    exercise_id: {
      type: String,
      required: true,
    },
    // Where this form is being rendered.
    // Possible values: demo, regimen_view, regimen_edit, printable
    context: {
      type: String,
      default: DEFAULT_EXERCISE_CONFIG_CONTEXT,
      validator: isExerciseConfigContext,
    },
    show_advanced: {
      type: Boolean,
      default: true,
    },
    customization: {
      type: String,
      default: "",
    },
  },
  computed: {
    ...mapState(["exercises", "app_config"]),
    localValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  mounted() {
    this.backfillMissingDefaults();
  },
  methods: {
    /**
     * Get the default config for this.exercise_id, including:
     * - global defaults from params.yml
     * - app-specific overrides (app_config)
     * - optional level-specific overrides (levelN) based on this.value.level
     * - defaults for this form's context (context_params)
     */
    getDefaultConfig() {
      if (!this.exercises || !this.exercise_id || !this.exercises[this.exercise_id]) {
        return {};
      }

      const exercise = this.exercises[this.exercise_id];

      // Determine which level to apply, if any, based on current value
      const levelToUse =
        this.value && this.value.level != null ? this.value.level : null;

      const appId = this.app_config && this.app_config.id ? this.app_config.id : null;
      return getExerciseDefaultConfig(exercise, appId, levelToUse, this.context);
    },

    backfillMissingDefaults() {
      if (!this.localValue || typeof this.localValue !== "object") {
        return;
      }

      const defaultConfig = this.getDefaultConfig();
      for (const [key, defaultValue] of Object.entries(defaultConfig)) {
        if (!Object.prototype.hasOwnProperty.call(this.localValue, key)) {
          Vue.set(this.localValue, key, defaultValue);
        }
      }
    },

    /**
     * Proxy validate to the inner exercise config, if it exists.
     * @param {object} config Exercise params to validate.
     * @param {import("../lib/exercise_config_context").ExerciseConfigContext} [context]
     *   Where validation is running, defaulting to this form's own context.
     *   `regimen_edit` means a stored exercise is being assigned or edited
     *   (the reading text is chosen at launch, not here).
     * @returns {string[]} Validation error messages.
     */
    validate(config, context) {
      const inner = this.$refs.inner;
      if (inner && typeof inner.validate === "function") {
        return inner.validate(config, context || this.context);
      }
      return [];
    },

    // Proxy applyDefaultUnits to the inner exercise config, if it exists
    applyDefaultUnits() {
      const inner = this.$refs.inner;
      if (inner && typeof inner.applyDefaultUnits === "function") {
        inner.applyDefaultUnits();
      }
    },
  },
};
</script>

